import { useState, useEffect } from 'react';
import { Select } from 'antd';
import { GetUniversitiesConfig, GetQuizRulesListConfig } from 'src/service/api';

const { Option } = Select;

interface HeaderProps {
  onMenuClick: () => void;
  selectedUniversity: string;
  setSelectedUniversity: (value: string) => void;
  selectedTopic: string;
  setSelectedTopic: (value: string) => void;
  setSelectedTopicName?: (value: string) => void;
  isCompleted?: boolean | undefined;
  setIsCompleted?: (value: boolean | undefined) => void;
}

interface University {
  id: string;
  name: string;
  code?: string;
}

interface Topic {
  id: string;
  name: string;
  theme?: string;
  hemisUserType?: string;
}

const STORAGE_KEYS = {
  SELECTED_UNIVERSITY: 'dashboard_selected_university',
  SELECTED_TOPIC: 'dashboard_selected_topic',
  IS_COMPLETED: 'dashboard_is_completed',
};

const Header = ({ onMenuClick, selectedUniversity, setSelectedUniversity, selectedTopic, setSelectedTopic, setSelectedTopicName, isCompleted, setIsCompleted }: HeaderProps) => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [surveyTopics, setSurveyTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // LocalStorage dan qiymatlarni yuklash
  useEffect(() => {
    const savedUniversity = localStorage.getItem(STORAGE_KEYS.SELECTED_UNIVERSITY);
    const savedTopic = localStorage.getItem(STORAGE_KEYS.SELECTED_TOPIC);
    const savedIsCompleted = localStorage.getItem(STORAGE_KEYS.IS_COMPLETED);
    
    if (savedUniversity) {
      setSelectedUniversity(savedUniversity);
    }
    if (savedTopic) {
      setSelectedTopic(savedTopic);
    }
    if (savedIsCompleted !== null && setIsCompleted) {
      setIsCompleted(savedIsCompleted === 'true' ? true : savedIsCompleted === 'false' ? false : undefined);
    }
    setIsInitialized(true);
  }, []);

  // Universitet o'zgarganda localStorage ga saqlash
  const handleUniversityChange = (value: string | undefined) => {
    const newValue = value || '';
    setSelectedUniversity(newValue);
    if (newValue) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_UNIVERSITY, newValue);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_UNIVERSITY);
    }
  };

  // Mavzu o'zgarganda localStorage ga saqlash
  const handleTopicChange = (value: string | undefined) => {
    const newValue = value || '';
    setSelectedTopic(newValue);
    
    // Topic nomini topish va set qilish
    const selectedTopicObj = surveyTopics.find(t => t.id === newValue);
    if (setSelectedTopicName) {
      setSelectedTopicName(selectedTopicObj?.name || selectedTopicObj?.theme || '');
    }
    
    if (newValue) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_TOPIC, newValue);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_TOPIC);
    }
  };

  // Yakunlangan filter o'zgarganda localStorage ga saqlash
  const handleIsCompletedChange = (value: string | undefined) => {
    let newValue: boolean | undefined = undefined;
    if (value === 'true') {
      newValue = true;
    } else if (value === 'false') {
      newValue = false;
    }
    
    if (setIsCompleted) {
      setIsCompleted(newValue);
      if (newValue !== undefined) {
        localStorage.setItem(STORAGE_KEYS.IS_COMPLETED, String(newValue));
      } else {
        localStorage.removeItem(STORAGE_KEYS.IS_COMPLETED);
      }
    }
  };

  useEffect(() => {
    const fetchUniversities = async () => {
      setLoading(true);
      try {
        const response = await GetUniversitiesConfig();
        if (response?.data) {
          const universitiesList = Array.isArray(response.data) 
            ? response.data.map((uni: any) => ({
                id: uni.code || uni.id || String(uni.universityCode || ''),
                name: uni.name || uni.universityName || ''
              }))
            : [];
          setUniversities(universitiesList);
        }
      } catch (error) {
        console.error('Error fetching universities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversities();
  }, []);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await GetQuizRulesListConfig();
        if (response?.data) {
          const topicsList = Array.isArray(response.data) 
            ? response.data.map((topic: any) => ({
                id: String(topic.id || ''),
                name: topic.theme || topic.name || topic.themeName || '',
                theme: topic.theme || topic.name || topic.themeName || '',
                hemisUserType: topic.hemisUserType || ''
              }))
            : [];
          setSurveyTopics(topicsList);
          
          // Agar hech qanday mavzu tanlanmagan bo'lsa va localStorageda ham yo'q bo'lsa,
          // birinchi mavzuni default qilib tanlaymiz
          if (isInitialized && topicsList.length > 0) {
            const savedTopic = localStorage.getItem(STORAGE_KEYS.SELECTED_TOPIC);
            if (!savedTopic && !selectedTopic) {
              const firstTopicId = topicsList[0].id;
              setSelectedTopic(firstTopicId);
              localStorage.setItem(STORAGE_KEYS.SELECTED_TOPIC, firstTopicId);
              // Topic nomini ham set qilish
              if (setSelectedTopicName) {
                setSelectedTopicName(topicsList[0].name || topicsList[0].theme || '');
              }
            } else {
              // Saqlangan topic uchun nomini topish
              const currentTopicId = savedTopic || selectedTopic;
              const currentTopic = topicsList.find(t => t.id === currentTopicId);
              if (setSelectedTopicName && currentTopic) {
                setSelectedTopicName(currentTopic.name || currentTopic.theme || '');
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching topics:', error);
      }
    };

    fetchTopics();
  }, [isInitialized]);

  return (
    <header className="dashboard-header">
      <div className="header-actions">
        {/* Filters */}
        <div className="filters-container">
          {/* University Filter */}
          <div className="filter-group">
            <div className="filter-wrapper university-wrapper">
              <Select
                value={selectedUniversity || undefined}
                onChange={handleUniversityChange}
                allowClear
                showSearch
                placeholder="Universitetni tanlang"
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                suffixIcon={null}
                bordered={false}
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={true}
              >
                {universities.map((uni) => (
                  <Option key={uni.id} value={uni.id}>
                    {uni.name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Survey Topics Filter */}
          <div className="filter-group">
            <div className="filter-wrapper topic-wrapper">
              <Select
                value={selectedTopic || undefined}
                onChange={handleTopicChange}
                allowClear
                showSearch
                placeholder="Mavzuni tanlang"
                filterOption={(input, option) =>
                  String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                }
                suffixIcon={null}
                bordered={false}
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={true}
              >
                {surveyTopics.map((topic) => {
                  const prefix = topic.hemisUserType === 'TEACHER' 
                    ? "O'qituvchilar uchun: " 
                    : topic.hemisUserType === 'STUDENT' 
                      ? "Talabalar uchun: " 
                      : '';
                  return (
                    <Option key={topic.id} value={topic.id}>
                      {prefix && <strong>{prefix}</strong>}{topic.theme}
                    </Option>
                  );
                })}
              </Select>
            </div>
          </div>

          {/* Yakunlangan Filter */}
          <div className="filter-group">
            <div className="filter-wrapper completed-wrapper">
              <Select
                value={isCompleted !== undefined ? String(isCompleted) : undefined}
                onChange={handleIsCompletedChange}
                allowClear
                placeholder="So'rovnoma holatini tanlang"
                suffixIcon={null}
                bordered={false}
                style={{ width: '100%' }}
                dropdownMatchSelectWidth={true}
              >
                <Option value="true">Yakunlangan</Option>
                <Option value="false">Jarayonda</Option>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
